import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getToken } from "./tokenStorage";
import { AuthCtx } from "./AuthProvider";

// ── маршруты, на которых могут появляться скрепки ────────────────────────────
const CLIP_ROUTES = ["/schedule", "/auditories", "/account", "/users", "/teacher-export"];

// Golden clip: ~1/14 окон для всех. Корона больше не влияет на спавн —
// награда за корону начисляется в конце недели по сумме окон владения
// (см. routers/crown.py: tenure_reward).
function getGoldenClipRoute(windowId = getWindowId(), userId = 0) {
  if (window.__forceGolden) return CLIP_ROUTES[0];
  let h = 0;
  for (const c of "golden" + windowId + String(userId)) h = ((h * 31 + c.charCodeAt(0)) >>> 0);
  if (h % 14 !== 0) return null;
  return CLIP_ROUTES[h % CLIP_ROUTES.length];
}

function goldenDailyKey(userId = 0) {
  return `skrep_golden_${getWindowId()}_${userId}`;
}

function isMobile() {
  return typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches;
}

// Must match backend _ZONE_LIST filtering in skrep.py _window_active_zones().
// Role-based (not device-based) so PC and mobile pick identical zones.
function getRoleClipList(role) {
  if (role === "admin" || role === "superadmin") return CLIP_ROUTES.filter(r => r !== "/users");
  if (role === "teacher") return CLIP_ROUTES.filter(r => r !== "/users");
  return CLIP_ROUTES.filter(r => r !== "/teacher-export"); // student / guest
}

// fix 4: superadmin gets same routes as admin
function getAccessibleRoutes(user, mobile) {
  const role = user?.role;
  if (mobile) {
    const routes = ["/auditories", "/schedule", "/account"];
    if (role === "teacher" || role === "admin" || role === "superadmin") routes.push("/teacher-export");
    else if (user) routes.push("/users");
    return routes;
  }
  if (role === "admin" || role === "superadmin") return ["/auditories", "/schedule", "/teacher-export", "/users", "/account"];
  if (role === "teacher") return ["/auditories", "/schedule", "/teacher-export", "/account"];
  if (user)               return ["/auditories", "/schedule", "/users", "/account"];
  return ["/schedule"];
}

function normalizeRoute(pathname) {
  if (!pathname) return "/";
  if (pathname.startsWith("/profile/")) return "/profile";
  return pathname;
}

// ── time-window helpers (меняется каждые 2 часа) ─────────────────────────────
function getWindowId() {
  if (window.__debugClipWindow) return window.__debugClipWindow;
  const param = new URLSearchParams(window.location.search).get("clipWindow");
  if (param) return param;
  // Нефтекамск UTC+5 — должно совпадать с backend `datetime.utcnow() + timedelta(hours=5)`
  const nowLocal = new Date(Date.now() + 5 * 3600 * 1000);
  return `${nowLocal.toISOString().split("T")[0]}_w${Math.floor(nowLocal.getUTCHours() / 2)}`;
}

function userSeed(extra = "", userId = 0) {
  const key = getWindowId() + extra + String(userId);
  return [...key].reduce((h, c) => ((h * 31 + c.charCodeAt(0)) >>> 0), 0);
}

function dailyKey(route, userId = 0) {
  const routeSlug = route.replace(/\//g, "_");
  return `skrep_clip_${getWindowId()}_${userId}_${routeSlug}`;
}

function dailyKeyByZone(zoneId, userId = 0) {
  return `skrep_clip_${getWindowId()}_${userId}_${zoneId.replace(/^clip_/, "")}`;
}

// 2–4 случайных маршрута за окно, уникально для каждого пользователя.
// Корона больше не влияет на количество зон — все играют одинаково.
// availableRoutes — pre-filtered by role so result is always accessible.
function getWindowClipRoutes(windowId = getWindowId(), userId = 0, availableRoutes = CLIP_ROUTES) {
  let h = 0;
  for (const c of windowId + String(userId)) h = ((h * 31 + c.charCodeAt(0)) >>> 0);
  const count = (h % 3) + 2;
  const routes = [...availableRoutes];
  let seed = h;
  for (let i = routes.length - 1; i > 0; i--) {
    seed = ((seed * 1664525 + 1013904223) >>> 0);
    const j = seed % (i + 1);
    [routes[i], routes[j]] = [routes[j], routes[i]];
  }
  return new Set(routes.slice(0, Math.min(count, routes.length)));
}

// BroadcastChannel for instant cross-tab sync (same browser only).
const _bc = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("skrep_sync") : null;
function _bcNotify() { try { _bc?.postMessage("collected"); } catch {} }

// ── golden collect ────────────────────────────────────────────────────────────
async function doCollectGolden(windowId = null) {
  const token = getToken();
  if (!token) return false;
  try {
    const r = await fetch("/skrep/collect-golden", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(windowId ? { window_id: windowId } : {}),
    });
    const data = await r.json();
    if (r.ok) {
      if (!data.already_collected) {
        _bcNotify();
        window.dispatchEvent(new CustomEvent("skrepCollected", { detail: data }));
        window.dispatchEvent(new CustomEvent("goldenClipCollected", { detail: data }));
        return "ok";
      }
      return "skip";
    }
    return false;
  } catch {
    return false;
  }
}

// ── collect ──────────────────────────────────────────────────────────────────
async function doCollect(zone, windowId = null) {
  const token = getToken();
  if (!token) return false;
  try {
    const r = await fetch("/skrep/collect", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ zone_id: zone, ...(windowId ? { window_id: windowId } : {}) }),
    });
    const data = await r.json();
    if (r.ok) {
      if (!data.already_collected) {
        _bcNotify();
        window.dispatchEvent(new CustomEvent("skrepCollected", { detail: data }));
        return "ok";
      }
      return "skip";
    }
    return false;
  } catch {
    return false;
  }
}

function playCollectPip() {
  if (Math.random() < 0.1) {
    try {
      const a = new Audio('/hehe_sound.mp3');
      a.volume = 0.6;
      a.play().catch(() => {});
    } catch {}
  } else {
    try {
      const ctx = new (window.AudioContext || /** @type {any} */(window).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
      osc.start();
      osc.stop(ctx.currentTime + 0.18);
    } catch {}
  }
  navigator.vibrate?.([30]);
}

// fix 1: inject ANIM_CSS once into <head> at module load, not per-clip
const ANIM_CSS = `
  @keyframes cp-float   { 0%,100%{transform:var(--cpr) translateY(0)}    50%{transform:var(--cpr) translateY(-5px)} }
  @keyframes cp-peek    { 0%,100%{transform:var(--cpr) translateX(0)}    40%,60%{transform:var(--cpr) translateX(-6px)} }
  @keyframes cp-collect { 0%{opacity:1;transform:var(--cpr) scale(1)}    55%{opacity:.8;transform:rotate(0) scale(1.6)}  100%{opacity:0;transform:rotate(25deg) scale(0)} }
  @keyframes cp-label   { 0%{opacity:1;transform:translateX(-50%) translateY(0)} 100%{opacity:0;transform:translateX(-50%) translateY(-18px)} }
`;
if (typeof document !== "undefined" && !document.getElementById("cp-anim-css")) {
  const s = document.createElement("style");
  s.id = "cp-anim-css";
  s.textContent = ANIM_CSS;
  document.head.appendChild(s);
}

const CLIP_PX = 32; // w-8
const CLIP_Z  = 40; // above sticky headers (z-30), below dropdowns (z-50) and mobile-nav (z-65)

// ── camouflage: stick to account tab icon in mobile nav ──────────────────────
function getCamouflagePos() {
  const el = document.querySelector('nav[aria-label="Основная навигация"] a[href="/account"]');
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  // Element hidden (display:none on desktop — sm:hidden) → rect is all zeros
  if (rect.width === 0 || rect.height === 0) return null;
  return {
    top:  rect.top  + (rect.height - CLIP_PX) / 2,
    left: rect.left + (rect.width  - CLIP_PX) / 2,
    rotate: "0deg",
    anim: null,
    fixed: true,
  };
}

// ── embed: clip fully inside a UI element ────────────────────────────────────

function getScrollParent(el) {
  let p = el.parentElement;
  while (p && p !== document.body) {
    const { overflowX, overflowY } = window.getComputedStyle(p);
    if (/auto|scroll/.test(overflowX) || /auto|scroll/.test(overflowY)) return p;
    p = p.parentElement;
  }
  return null;
}

// Priority selectors — checked in order; first non-empty set wins.
const EMBED_SELECTORS = [
  // schedule cells / pair cards
  'td, th, [class*="pair"], [class*="cell"], [class*="lesson"], [class*="schedule"] li',
  // generic cards / list items with enough room
  'li, article, [role="listitem"], [role="row"]',
  // any sized block inside main
  'main *, section *, [role="main"] *',
];
const EMBED_EXCLUDED = new Set(['button','a','input','select','textarea','label','nav','header','footer','script','style','svg','path','img','span']);

export function getEmbedPos(seed, filterZone = null) {
  const PAD = 8;
  const vwOuter = window.innerWidth;
  console.log(`[clip] getEmbedPos seed=${seed} filterZone=${filterZone}`);

  // ── special: avatar zones — place clip centred on a seed-selected avatar ─────
  if (filterZone === "users_avatar") {
    const avatarZones = [...document.querySelectorAll('[data-clip-zone="users_avatar"]')]
      .filter(el => { const r = el.getBoundingClientRect(); return r.width > 0 && r.height > 0; });
    if (!avatarZones.length) { console.log("[clip] users_avatar: no zones yet"); return null; }
    const zone  = avatarZones[(seed >> 8) % avatarZones.length];
    const r     = zone.getBoundingClientRect();
    const scrollY = window.scrollY || 0;
    const cx    = r.left + r.width  / 2 - CLIP_PX / 2 + (((seed >> 4)  % 16) - 8);
    const cy    = r.top  + scrollY  + r.height / 2 - CLIP_PX / 2 + (((seed >> 12) % 16) - 8);
    const tilt  = ((seed >> 20) % 25) - 12;
    console.log(`[clip] users_avatar: zone ${zone.dataset.uid} top=${Math.round(cy)} left=${Math.round(cx)}`);
    return {
      top:    Math.max(scrollY + 52, cy),
      left:   Math.max(PAD, Math.min(vwOuter - CLIP_PX - PAD, cx)),
      rotate: `${tilt}deg`,
      anim:   null,
      fixed:  false,
      portal: document.body,
    };
  }

  // ── priority: zone-specific placement ────────────────────────────────────────
  {
    const zoneSelector = filterZone
      ? `[data-clip-zone="${filterZone}"]`
      : '[data-clip-zone]';
    const zones = document.querySelectorAll(zoneSelector);
    console.log(`[clip] zone selector="${zoneSelector}" found=${zones.length}`);

    if (zones.length > 0) {
      for (const zone of zones) {
        const searchRoots = [];
        let sib = zone.nextElementSibling;
        while (sib) { searchRoots.push(sib); sib = sib.nextElementSibling; }
        if (!searchRoots.length && zone.parentElement) searchRoots.push(zone.parentElement);

        for (const root of searchRoots) {
          for (const el of [root, ...root.querySelectorAll('div, li, article')]) {
            const r = el.getBoundingClientRect();
            if (r.width < CLIP_PX + PAD * 2 || r.height < CLIP_PX + PAD * 2) continue;
            if (r.bottom < 52) continue;
            if (r.right < 0 || r.left > vwOuter) continue;
            const availW = r.width - CLIP_PX - PAD * 2;
            const availH = r.height - CLIP_PX - PAD * 2;
            const fracX = ((seed >> 4)  % 80 + 10) / 100;
            const fracY = ((seed >> 12) % 80 + 10) / 100;
            const tilt  = ((seed >> 20) % 25) - 12;
            const cvx = Math.max(PAD, Math.min(vwOuter - CLIP_PX - PAD, r.left + PAD + fracX * availW));
            const cvy = Math.max(52,  Math.min(window.innerHeight - CLIP_PX - PAD, r.top + PAD + fracY * availH));
            return { top: cvy - r.top, left: cvx - r.left,
              rotate: `${tilt}deg`, anim: null, fixed: false, portal: el };
          }
        }
      }
    }

    if (filterZone) { console.log(`[clip] filterZone="${filterZone}" → no element, returning null`); return null; }
  }

  // ── priority: mobile lesson cards ──
  // Prefer collapsed cards (need tap to reveal); fall back to any card with content.
  // iOS WebKit: shell.scrollHeight returns 0 when ancestor [data-expand-inner] has
  // max-height:0 + overflow:hidden + isolation:isolate. Measure after temporarily
  // unsetting the ancestor's max-height so scrollHeight reports natural content height.
  const measureShellHeight = (shell) => {
    const inner = shell.closest('[data-expand-inner]');
    if (!inner) return shell.scrollHeight;
    const prevMax = inner.style.maxHeight;
    inner.style.maxHeight = 'none';
    // Force reflow before reading
    const h = shell.scrollHeight;
    inner.style.maxHeight = prevMax;
    return h;
  };
  for (const selector of [
    '[data-lesson-card]:not([data-expanded]) [data-expand-shell]',
    '[data-lesson-card] [data-expand-shell]',
  ]) {
    const shells = document.querySelectorAll(selector);
    const viable = [];
    for (const shell of shells) {
      if (measureShellHeight(shell) > CLIP_PX + PAD * 2) viable.push(shell);
    }
    if (viable.length) {
      const shell = viable[seed % viable.length];
      const sw   = Math.max(shell.scrollWidth, shell.offsetWidth);
      const sh   = measureShellHeight(shell);
      const availW = sw - CLIP_PX - PAD * 2;
      const availH = sh - CLIP_PX - PAD * 2;
      if (availW > 0 && availH > 0) {
        const fracX = ((seed >> 4)  % 80 + 10) / 100;
        const fracY = ((seed >> 12) % 80 + 10) / 100;
        const tilt  = ((seed >> 20) % 25) - 12;
        return {
          top: PAD + fracY * availH,
          left: PAD + fracX * availW,
          rotate: `${tilt}deg`,
          anim: null,
          fixed: false,
          portal: shell,
        };
      }
    }
  }

  // ── generic: any visible content element ─────────────────────────────────────
  const vw = vwOuter;

  let candidates = [];
  for (const selector of EMBED_SELECTORS) {
    for (const el of document.querySelectorAll(selector)) {
      if (candidates.length >= 80) break;
      const tag = el.tagName.toLowerCase();
      if (EMBED_EXCLUDED.has(tag)) continue;
      if (el.closest('button, a, input, select, nav, header, footer, [role="navigation"]')) continue;
      const r = el.getBoundingClientRect();
      if (r.width < CLIP_PX + PAD * 2 || r.height < CLIP_PX + PAD * 2) continue;
      if (r.bottom < 52) continue;
      if (r.right < 0 || r.left > vw) continue;
      candidates.push({ el, r });
    }
    if (candidates.length >= 3) break;
  }
  console.log(`[clip] generic candidates=${candidates.length} vw=${vw} vh=${window.innerHeight}`);
  if (!candidates.length) {
    // Guaranteed fallback: place at a seed-based position within the current viewport.
    // Ensures clip is always visible regardless of page DOM state.
    const vh = window.innerHeight;
    const navH = vw < 640 ? 64 : 0;
    const scrollY = window.scrollY || 0;
    const fracX = ((seed >> 4)  % 70 + 15) / 100;
    const fracY = ((seed >> 12) % 60 + 20) / 100;
    const tilt  = ((seed >> 20) % 25) - 12;
    const top  = scrollY + 52 + fracY * (vh - 52 - navH - CLIP_PX);
    const left = PAD + fracX * (vw - CLIP_PX - PAD * 2);
    console.log(`[clip] fallback body pos top=${Math.round(top)} left=${Math.round(left)}`);
    return { top, left, rotate: `${tilt}deg`, anim: null, fixed: false, portal: document.body };
  }

  const { el, r: rect } = candidates[seed % candidates.length];
  const availW = rect.width  - CLIP_PX - PAD * 2;
  const availH = rect.height - CLIP_PX - PAD * 2;
  const fracX = ((seed >> 4)  % 80 + 10) / 100;
  const fracY = ((seed >> 12) % 80 + 10) / 100;
  const tilt  = ((seed >> 20) % 25) - 12;

  // Always portal inside the page (never document.body) so the clip stays in the
  // same stacking context as fixed panels (z-70 skrep/devices panels etc.).
  // page-transition-shell has will-change:transform → creates stacking ctx at z:auto.
  // A body portal at z:20 in root would float ABOVE that z:auto context — wrong.
  const scrollParent = getScrollParent(el);
  const portal = scrollParent
    || el.closest('.page-transition-shell, main, [role="main"], section')
    || el.parentElement
    || document.body;

  const cRect = portal === document.body
    ? { left: 0, top: 0 }
    : portal.getBoundingClientRect();
  const sl = (portal === document.body ? 0 : portal.scrollLeft) || 0;
  const st = (portal === document.body ? (window.scrollY || 0) : portal.scrollTop) || 0;

  const cvh  = window.innerHeight;
  const cvx  = Math.max(PAD, Math.min(vw - CLIP_PX - PAD, rect.left + PAD + fracX * availW));
  const cvy  = Math.max(52,  Math.min(cvh - CLIP_PX - PAD, rect.top  + PAD + fracY * availH));
  const left = cvx - cRect.left + sl;
  const top  = cvy - cRect.top  + st;
  return { top, left, rotate: `${tilt}deg`, anim: null, fixed: false, portal };
}

// Sets position:relative on portal element if needed (so absolute children are anchored to it).
function usePortalEl() {
  const ref = useRef(null);
  const saved = useRef(null);
  function attach(el) {
    if (!el || el === document.body) return;
    const cs = window.getComputedStyle(el);
    if (cs.position === 'static') {
      saved.current = { el, pos: el.style.position };
      el.style.position = 'relative';
    }
    ref.current = el;
  }
  function detach() {
    if (saved.current) {
      saved.current.el.style.position = saved.current.pos;
      saved.current = null;
    }
    ref.current = null;
  }
  useEffect(() => () => detach(), []); // eslint-disable-line react-hooks/exhaustive-deps
  return { attach, detach };
}

// Увеличиваем счётчик refresh для юзера+окна. Используется в seed позиции,
// чтобы при обновлении страницы клипс спавнился в другом месте
// (если застрял в неудобной позиции).
function bumpRefreshCounter(windowId, userId, route, filterZone) {
  const key = `clip_refresh_${windowId}_${userId}_${route}_${filterZone || "null"}`;
  const n = parseInt(localStorage.getItem(key) || "0", 10) + 1;
  try { localStorage.setItem(key, String(n)); } catch {}
  return n;
}

// ── single clip ───────────────────────────────────────────────────────────────
function RandomClip({ route, userId, seedExtra = "", filterZone = null, zoneId = null }) {
  const [absPos, setAbsPos]   = useState(null);
  const [done, setDone]       = useState(false);
  const [label, setLabel]     = useState("+1");
  const [zoneSeq, setZoneSeq] = useState(0);
  const busyRef              = useRef(false);
  const spawnWindowRef       = useRef(getWindowId());
  const portalRef            = useRef(null);
  const refreshNoRef         = useRef(bumpRefreshCounter(getWindowId(), userId, route, filterZone));
  const { attach, detach }   = usePortalEl();

  // Watch DOM: hide clip when portal detaches (tab unmounts), re-trigger when new zone appears
  useEffect(() => {
    let contentDebounce = null;
    const obs = new MutationObserver((mutations) => {
      // Portal detached → hide clip and re-trigger compute
      if (portalRef.current && portalRef.current !== document.body &&
          !document.contains(portalRef.current)) {
        portalRef.current = null;
        setAbsPos(null);
        setZoneSeq(s => s + 1);
        return;
      }
      // Zone attribute removed (e.g. building tab switch) → hide and re-trigger
      if (filterZone && filterZone !== "users_avatar" && portalRef.current &&
          !document.querySelector(`[data-clip-zone="${filterZone}"]`)) {
        detach();
        portalRef.current = null;
        setAbsPos(null);
        setZoneSeq(s => s + 1);
        return;
      }
      // <dialog> closed → hide clip if portal is inside that dialog
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'open' && !m.target.open) {
          if (portalRef.current && m.target.contains(portalRef.current)) {
            detach();
            portalRef.current = null;
            setAbsPos(null);
            setZoneSeq(s => s + 1);
            return;
          }
        }
      }
      if (portalRef.current) return; // already placed — ignore DOM churn
      for (const m of mutations) {
        // <dialog> opened → elements inside became visible (non-zero rect)
        if (m.type === 'attributes' && m.attributeName === 'open' && m.target.open) {
          if (contentDebounce) clearTimeout(contentDebounce);
          contentDebounce = setTimeout(() => setZoneSeq(s => s + 1), 150);
          return;
        }
        for (const n of m.addedNodes) {
          if (n.nodeType !== 1) continue;
          const tag = n.tagName?.toLowerCase();
          const isZone    = n.hasAttribute?.('data-clip-zone') || n.querySelector?.('[data-clip-zone]');
          // Substantial content: list, table, or div with 3+ children
          const isContent = tag === 'ul' || tag === 'ol' || tag === 'table' ||
                            (tag === 'div' && n.children?.length >= 3);
          if (isZone || isContent) {
            if (contentDebounce) clearTimeout(contentDebounce);
            contentDebounce = setTimeout(() => setZoneSeq(s => s + 1), 150);
            return;
          }
        }
      }
    });
    // attributes: catches data-clip-zone removal (building tab) + dialog open
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-clip-zone', 'open'] });
    return () => { obs.disconnect(); if (contentDebounce) clearTimeout(contentDebounce); };
  }, [filterZone]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveZoneId = zoneId || `clip_${route.replace(/^\//, "").replace(/[\/-]/g, "_")}`;
  const claimedKey = zoneId ? () => dailyKeyByZone(zoneId, userId) : () => dailyKey(route, userId);

  useEffect(() => {
    if (!getToken()) return;
    if (done) return; // collected — never respawn
    if (!seedExtra && localStorage.getItem(claimedKey())) return;
    if (portalRef.current && document.contains(portalRef.current)) return;

    let spawned = false;

    const compute = () => {
      if (spawned) return;
      console.log(`[clip] compute route=${route} filterZone=${filterZone} t=${Date.now()}`);
      detach();
      if (!filterZone && route === "/account" && userSeed("camouflage" + seedExtra, userId) % 3 === 0) {
        const pos = getCamouflagePos();
        if (pos) {
          console.log(`[clip] placed via camouflage`);
          spawned = true;
          portalRef.current = document.body;
          setAbsPos({ ...pos, portal: document.body });
          return;
        }
      }
      const pos = getEmbedPos(userSeed("embed_pos" + route + seedExtra + (filterZone || "") + "r" + refreshNoRef.current, userId), filterZone);
      if (!pos) { console.warn(`[clip] getEmbedPos returned null, will retry`); return; }
      console.log(`[clip] placed! portal=`, pos.portal?.tagName, pos.portal?.className?.slice?.(0,40));
      spawned = true;
      attach(pos.portal);
      portalRef.current = pos.portal;
      setAbsPos(pos);
    };

    // Fallback: если за 5с filter zone не нашёлся (пикер/магазин не открыт),
    // спавним клипс без filter → generic placement на видимом месте.
    // Гарантирует что юзер всегда получает возможность забрать скрепку.
    const computeFallback = () => {
      if (spawned) return;
      console.log(`[clip] 5s fallback: ignoring filterZone=${filterZone}`);
      detach();
      const pos = getEmbedPos(userSeed("embed_pos_fallback" + route + seedExtra + "r" + refreshNoRef.current, userId), null);
      if (!pos) { console.warn(`[clip] fallback also returned null`); return; }
      spawned = true;
      attach(pos.portal);
      portalRef.current = pos.portal;
      setAbsPos(pos);
    };

    window.addEventListener("pageContentReady", compute);
    const t0 = setTimeout(compute, 0);
    const t1 = setTimeout(compute, 100);
    const t2 = setTimeout(compute, 350);
    const t3 = setTimeout(compute, 900);
    const t4 = filterZone && filterZone !== "users_avatar"
      ? setTimeout(computeFallback, 5000)
      : null;
    return () => {
      window.removeEventListener("pageContentReady", compute);
      clearTimeout(t0); clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      if (t4) clearTimeout(t4);
    };
  }, [route, userId, seedExtra, zoneSeq, done]); // eslint-disable-line react-hooks/exhaustive-deps

  const collect = async () => {
    if (busyRef.current || done) return;
    if (getWindowId() !== spawnWindowRef.current) { setAbsPos(null); return; }
    busyRef.current = true;
    if (window.__debugClipWindow) {
      playCollectPip();
      setLabel("+1 [debug]");
      setDone(true);
      setTimeout(() => setAbsPos(null), 1000);
      return;
    }
    const result = await doCollect(effectiveZoneId, spawnWindowRef.current);
    if (result === "ok") {
      playCollectPip();
      localStorage.setItem(claimedKey(), "1");
      setLabel("+1");
      setDone(true);
      setTimeout(() => setAbsPos(null), 1000);
    } else if (result === "skip") {
      localStorage.setItem(claimedKey(), "1");
      setLabel("✓");
      setDone(true);
      setTimeout(() => setAbsPos(null), 800);
    } else {
      busyRef.current = false;
    }
  };

  if (!absPos) return null;

  const animStyle = done
    ? "cp-collect 0.85s ease-in forwards"
    : absPos.anim
      ? `${absPos.anim} 3s ease-in-out infinite`
      : "none";

  return createPortal(
    <button
      onClick={collect}
      title="Скрепка!"
      className={`p-0 bg-transparent border-0 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400 ${absPos.fixed ? "fixed" : "absolute"}`}
      style={{ top: absPos.top, left: absPos.left, "--cpr": `rotate(${absPos.rotate})`, zIndex: CLIP_Z }}
    >
      <img src="/paperclip.svg" alt="Скрепка" className="w-8 h-8 object-contain select-none drop-shadow" style={{ animation: animStyle }} />
      {done && (
        <span className="absolute -top-6 left-1/2 text-[12px] font-bold pointer-events-none whitespace-nowrap"
          style={{ animation: "cp-label 0.9s ease-out forwards", transform: "translateX(-50%)", color: label === "+1" ? "#f59e0b" : "#94a3b8" }}>
          {label}
        </span>
      )}
    </button>,
    absPos.portal || document.body
  );
}

// ── golden clip ───────────────────────────────────────────────────────────────
function GoldenClip({ route, userId }) {
  const [absPos, setAbsPos]   = useState(null);
  const [done, setDone]       = useState(false);
  const [label, setLabel]     = useState("✨");
  const [zoneSeq, setZoneSeq] = useState(0);
  const busyRef              = useRef(false);
  const spawnWindowRef       = useRef(getWindowId());
  const portalRef            = useRef(null);
  const { attach, detach }   = usePortalEl();

  useEffect(() => {
    const obs = new MutationObserver((mutations) => {
      if (portalRef.current && portalRef.current !== document.body &&
          !document.contains(portalRef.current)) {
        portalRef.current = null;
        setAbsPos(null);
        setZoneSeq(s => s + 1);
        return;
      }
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'open') {
          setZoneSeq(s => s + 1);
          return;
        }
        for (const n of m.addedNodes) {
          if (n.nodeType === 1 &&
              (n.hasAttribute?.('data-clip-zone') || n.querySelector?.('[data-clip-zone]'))) {
            setZoneSeq(s => s + 1);
            return;
          }
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!getToken()) return;
    if (done) return; // collected — never respawn
    if (!window.__forceGolden && localStorage.getItem(goldenDailyKey(userId))) return;
    if (portalRef.current && document.contains(portalRef.current)) return;
    let spawned = false;

    const compute = () => {
      if (spawned) return;
      detach();
      const pos = getEmbedPos(userSeed("embed_gpos" + route, userId));
      if (!pos) return;
      spawned = true;
      attach(pos.portal);
      portalRef.current = pos.portal;
      setAbsPos(pos);
    };

    window.addEventListener("pageContentReady", compute);
    const t0 = setTimeout(compute, 0);
    const t1 = setTimeout(compute, 100);
    const t2 = setTimeout(compute, 350);
    const t3 = setTimeout(compute, 900);
    return () => {
      window.removeEventListener("pageContentReady", compute);
      clearTimeout(t0); clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
    };
  }, [route, userId, zoneSeq, done]); // eslint-disable-line react-hooks/exhaustive-deps

  const collect = async () => {
    if (busyRef.current || done) return;
    if (getWindowId() !== spawnWindowRef.current) { setAbsPos(null); return; }
    busyRef.current = true;
    if (window.__debugClipWindow) {
      playCollectPip();
      setLabel("✨+1 [debug]");
      setDone(true);
      setTimeout(() => setAbsPos(null), 1000);
      return;
    }
    const result = await doCollectGolden(spawnWindowRef.current);
    if (result === "ok") {
      playCollectPip();
      localStorage.setItem(goldenDailyKey(userId), "1");
      setLabel("✨+1");
      setDone(true);
      setTimeout(() => setAbsPos(null), 1000);
    } else if (result === "skip") {
      localStorage.setItem(goldenDailyKey(userId), "1");
      setLabel("✓");
      setDone(true);
      setTimeout(() => setAbsPos(null), 800);
    } else {
      busyRef.current = false;
    }
  };

  if (!absPos) return null;

  const animStyle = done
    ? "cp-collect 0.85s ease-in forwards"
    : absPos.anim
      ? `${absPos.anim} 2.5s ease-in-out infinite`
      : "none";

  return createPortal(
    <button
      onClick={collect}
      title="Золотая скрепка! 🌟"
      className={`p-0 bg-transparent border-0 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-400 ${absPos.fixed ? "fixed" : "absolute"}`}
      style={{
        top: absPos.top, left: absPos.left, "--cpr": `rotate(${absPos.rotate})`,
        zIndex: CLIP_Z + 1,
        filter: done ? "none" : "drop-shadow(0 0 6px #fbbf24) drop-shadow(0 0 12px #f59e0b80)",
      }}
    >
      <img src="/golden_paperclip.svg" alt="Золотая скрепка" className="w-8 h-8 object-contain select-none" style={{ animation: animStyle }} />
      {done && (
        <span className="absolute -top-6 left-1/2 text-[12px] font-bold pointer-events-none whitespace-nowrap"
          style={{ animation: "cp-label 0.9s ease-out forwards", transform: "translateX(-50%)", color: "#fbbf24" }}>
          {label}
        </span>
      )}
    </button>,
    absPos.portal || document.body
  );
}

// ── entry point ───────────────────────────────────────────────────────────────
export function DailyPaperclipsGlobal({ route }) {
  const [windowId, setWindowId] = useState(getWindowId);
  const [claimsReady, setClaimsReady] = useState(false);
  const [claimedZones, setClaimedZones] = useState(() => new Set());
  const { user } = useContext(AuthCtx);

  const applyZones = useCallback((zones, uid) => {
    const s = new Set(zones || []);
    (zones || []).forEach(zId => {
      if (zId === "clip_golden") { localStorage.setItem(goldenDailyKey(uid), "1"); return; }
      // Sub-zone keys (clip_account_shop / clip_account_collection)
      if (zId === "clip_account_shop" || zId === "clip_account_collection") {
        localStorage.setItem(dailyKeyByZone(zId, uid), "1");
        return;
      }
      const r = CLIP_ROUTES.find(cr => `clip_${cr.replace(/^\//, "").replace(/[\/-]/g, "_")}` === zId);
      if (r) localStorage.setItem(dailyKey(r, uid), "1");
    });
    setClaimedZones(s);
  }, []);

  const syncClaims = useCallback((uid) => {
    if (!uid) return;
    if (window.__simulateDailySpawn) return;
    const token = getToken();
    if (!token) return;
    const wid = encodeURIComponent(getWindowId());
    fetch(`/skrep/claimed-today?window_id=${wid}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { zones: [] })
      .then(data => applyZones(data.zones, uid))
      .catch(() => {});
  }, [applyZones]);

  useEffect(() => {
    const updateWindow = () => {
      const w = getWindowId();
      setWindowId(prev => prev !== w ? w : prev);
    };
    const onVisibility = () => {
      updateWindow();
      if (document.visibilityState === "visible") syncClaims(user?.id);
    };
    const onFocus = () => { updateWindow(); syncClaims(user?.id); };
    const onDebugReset = (e) => {
      if (e.detail?.action === "clearClaims") setClaimedZones(new Set());
      setWindowId(getWindowId());
    };
    const onBc = () => syncClaims(user?.id);
    const t = setInterval(() => { updateWindow(); syncClaims(user?.id); }, 4_000);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("debugClipReset", onDebugReset);
    _bc?.addEventListener("message", onBc);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("debugClipReset", onDebugReset);
      _bc?.removeEventListener("message", onBc);
    };
  }, [user?.id, syncClaims]);

  useEffect(() => {
    let cancelled = false;
    setClaimsReady(false);
    setClaimedZones(new Set());
    const token = getToken();
    if (!token || window.__debugClipWindow || window.__simulateDailySpawn) { setClaimsReady(true); return; }
    const uid = user?.id || 0;
    const wid = encodeURIComponent(getWindowId());
    (async () => {
      try {
        const r = await fetch(`/skrep/claimed-today?window_id=${wid}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = r.ok ? await r.json() : { zones: [] };
        if (!cancelled) { applyZones(data.zones, uid); setClaimsReady(true); }
      } catch {
        if (!cancelled) {
          // network failure — restore from localStorage so claimed clips stay hidden
          const local = new Set(
            CLIP_ROUTES
              .filter(route => localStorage.getItem(dailyKey(route, uid)))
              .map(route => `clip_${route.replace(/^\//, "").replace(/[\/-]/g, "_")}`)
          );
          if (localStorage.getItem(goldenDailyKey(uid))) local.add("clip_golden");
          setClaimedZones(local);
          setClaimsReady(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, windowId, applyZones]);

  // fix 8: don't render with userId=0 — wait for real user to prevent cache-key collision
  if (!claimsReady || !user) return null;

  const userId = user.id;
  const normalized = normalizeRoute(route);
  const accessible = getAccessibleRoutes(user, isMobile());
  // Shuffle role-based list (matches backend) so PC and mobile pick identical zones.
  // Device accessibility check is done at render time.
  const clipRoutes = getWindowClipRoutes(windowId, userId, getRoleClipList(user.role));
  const goldenRoute = getGoldenClipRoute(windowId, userId);
  const roleRoutes = getRoleClipList(user.role);
  const showGolden = goldenRoute === normalized && roleRoutes.includes(normalized);
  console.log(`[clip] window=${windowId} route=${normalized} active=[${[...clipRoutes].join(",")}] thisRouteHasClip=${clipRoutes.has(normalized)} mobile=${isMobile()}`);

  const zoneId = `clip_${normalized.replace(/^\//, "").replace(/[\/-]/g, "_")}`;
  const forceClip = new URLSearchParams(window.location.search).has("forceClip") || !!window.__forceClip;

  // Debug: one clip per zone so all spawn locations are visible at once
  const DEBUG_ZONE_MAP = {
    "/account":    [{ fz: "shop", zId: "clip_account_shop" }, { fz: "collection", zId: "clip_account_collection" }],
    "/auditories": [{ fz: "auditories_b2" }, { fz: "free_auditories" }, { fz: "teacher_search" }, { fz: null }],
    "/users":      [{ fz: "users_list" }, { fz: "users_avatar" }, { fz: null }],
    "/schedule":   isMobile() ? [{ fz: "schedule_calendar" }, { fz: "schedule_picker" }, { fz: null }] : [{ fz: null }],
  };

  return (
    <>
      {!window.__debugClipWindow && (forceClip || clipRoutes.has(normalized)) && (
        normalized === "/account" ? <>
          {!claimedZones.has("clip_account_shop") && (
            <RandomClip key={"ras" + windowId + userId} route="/account" userId={userId}
              filterZone="shop" zoneId="clip_account_shop" />
          )}
          {!claimedZones.has("clip_account_collection") && (
            <RandomClip key={"rac" + windowId + userId} route="/account" userId={userId}
              filterZone="collection" zoneId="clip_account_collection" />
          )}
        </> : !claimedZones.has(zoneId) && (
          <RandomClip key={"r" + windowId + userId} route={normalized} userId={userId}
            filterZone={
              normalized === "/auditories"
                ? (["auditories_b2", "free_auditories", "teacher_search", null][userSeed("aud_zone_" + windowId, userId) % 4])
              : normalized === "/users"
                ? (["users_list", "users_avatar", null][userSeed("users_zone_" + windowId, userId) % 3])
              : normalized === "/schedule" && isMobile()
                ? (["schedule_calendar", "schedule_picker", null][userSeed("sched_zone_" + windowId, userId) % 3])
              : null
            } />
        )
      )}
      {window.__debugClipWindow && accessible.includes(normalized) &&
        (DEBUG_ZONE_MAP[normalized] || [{ fz: null }]).map(({ fz, zId }, i) => (
          <RandomClip key={"dbg" + i + windowId} route={normalized} userId={userId}
            seedExtra={"dbg" + i} filterZone={fz ?? null} zoneId={zId ?? undefined} />
        ))
      }
      {(showGolden || (window.__debugClipWindow && window.__forceGolden)) && !claimedZones.has("clip_golden") && (
        <GoldenClip key={"g" + windowId + userId} route={normalized} userId={userId} />
      )}
    </>
  );
}
